import { Book, Calendar } from "lucide-react";
import { Course } from "@/types/courseTypes";
import CourseCard from "./CourseCard";

interface CourseListProps {
  courses: Course[];
  isLoading: boolean;
  courseType: "theory" | "practical" | "individual";
  isEnrolled: (courseId: string) => boolean;
  onViewCourse: (courseId: string, courseType: string) => void;
  enrollments?: any[]; // optional: array of enrollment details with courseId
}

const CourseList = ({
  courses,
  isLoading,
  courseType,
  isEnrolled,
  onViewCourse,
  enrollments = [],
}: CourseListProps) => {
  // Filter courses to only include the matching type
  const filteredCourses = courses.filter((course) => 
    courseType === "individual" ? course.subtype === "individual" : course.type === courseType
  );

  // Helper: for a given courseId, find their enrollment info from props.enrollments
  const findEnrollment = (courseId: string) => {
    const enrollment = enrollments.find((e) => e.courseId === courseId);
    // Only return enrollments with a valid enrollmentStatus
    return enrollment && enrollment.enrollmentStatus && 
      ['pending', 'active', 'completed'].includes(enrollment.enrollmentStatus) 
      ? enrollment 
      : undefined;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <p>Loading courses...</p>
      </div>
    );
  }

  if (filteredCourses.length === 0) {
    return (
      <div className="text-center py-12">
        {courseType === "theory" ? (
          <Book className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        ) : (
          <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        )}
        <h3 className="text-lg font-medium text-gray-900">
          No {courseType} courses found
        </h3>
        <p className="text-gray-500 mt-2 max-w-md mx-auto">
          We couldn't find any {courseType} courses matching your search criteria.
        </p>
      </div>
    );
  }

  return (
    <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
      {filteredCourses.map((course) => {
        const enrolled = isEnrolled(course.id);
        const enrollment = enrolled ? findEnrollment(course.id) : undefined;
        
        return (
          <CourseCard
            key={course.id}
            course={course}
            isEnrolled={enrolled}
            onViewCourse={() => onViewCourse(course.id, course.type)}
            enrollment={enrollment}
          />
        );
      })}
    </div>
  );
};

export default CourseList;
